import React, { useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, View, LayoutChangeEvent } from 'react-native';
import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { PlatformPressable } from '@react-navigation/elements';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import getTabBarIcon from './TabBarIcon';
import tabNavigatorStyle from '@assets/styles/tabNavigatorStyles';
import { colorPalette } from '@shared/config/colorsConfig';
import { Text } from '@shared/components/text/Text';

const INDICATOR_MARGIN = 4;
const INDICATOR_SIDE_PADDING = 10;
const INDICATOR_TOP = 8;
const INDICATOR_BOTTOM_OFFSET = 14;
const INDICATOR_RADIUS = 16;
const TAB_ICON_SIZE = 24;
const ANIMATION_DURATION = 250;
const NATIVE_TAB_BAR_HEIGHT = 90;
const NATIVE_PADDING_BOTTOM = 20;
const WEB_TAB_BAR_HEIGHT = 96;
const WEB_PADDING_BOTTOM = 14;

const isWeb = Platform.OS === 'web';

type AnimatedTabBarProps = BottomTabBarProps & {
  renderTabAnchor?: (routeName: string) => React.ReactNode;
};

const AnimatedTabBar: React.FC<AnimatedTabBarProps> = ({ state, descriptors, navigation, renderTabAnchor }) => {
  const insets = useSafeAreaInsets();
  const [containerWidth, setContainerWidth] = useState(0);
  const indicatorX = useSharedValue(0);
  const hasMeasuredRef = useRef(false);

  const itemWidth = containerWidth > 0 ? containerWidth / state.routes.length : 0;

  const onContainerLayout = (e: LayoutChangeEvent) => {
    const width = e.nativeEvent.layout.width;
    setContainerWidth(width);
    if (!hasMeasuredRef.current) {
      hasMeasuredRef.current = true;
      indicatorX.value = state.routes.length > 0 ? (width / state.routes.length) * state.index : 0;
    }
  };

  React.useEffect(() => {
    if (!hasMeasuredRef.current || itemWidth === 0) return;
    indicatorX.value = withTiming(itemWidth * state.index, { duration: ANIMATION_DURATION });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.index, itemWidth]);

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: indicatorX.value }],
    width: itemWidth > 0 ? itemWidth - (INDICATOR_MARGIN + INDICATOR_SIDE_PADDING) * 2 : 0,
  }));

  const barStyle = useMemo(
    () => [
      tabNavigatorStyle.tabBar,
      styles.barRow,
      {
        height: (isWeb ? WEB_TAB_BAR_HEIGHT : NATIVE_TAB_BAR_HEIGHT) + (isWeb ? 0 : insets.bottom),
        paddingBottom: (isWeb ? WEB_PADDING_BOTTOM : NATIVE_PADDING_BOTTOM) + (isWeb ? 0 : insets.bottom),
      },
    ],
    [insets.bottom],
  );

  const indicatorContainerStyle = useMemo(
    () => [
      styles.indicatorBase,
      {
        bottom: INDICATOR_BOTTOM_OFFSET + (isWeb ? 0 : insets.bottom),
      },
    ],
    [insets.bottom],
  );

  const anchorOverlayStyle = useMemo(
    () => [
      styles.anchorOverlay,
      {
        bottom: INDICATOR_BOTTOM_OFFSET + (isWeb ? 0 : insets.bottom),
      },
    ],
    [insets.bottom],
  );

  return (
    <View onLayout={onContainerLayout} style={barStyle}>
      {itemWidth > 0 && <Animated.View pointerEvents="none" style={[indicatorContainerStyle, indicatorStyle]} />}
      {renderTabAnchor &&
        itemWidth > 0 &&
        state.routes.map((route, index) => {
          const overlay = renderTabAnchor(route.name);

          return overlay ? (
            <View
              key={`tour-anchor-${route.key}`}
              pointerEvents="none"
              style={[
                anchorOverlayStyle,
                {
                  left: INDICATOR_MARGIN + INDICATOR_SIDE_PADDING + itemWidth * index,
                  width: itemWidth - (INDICATOR_MARGIN + INDICATOR_SIDE_PADDING) * 2,
                },
              ]}
            >
              {overlay}
            </View>
          ) : null;
        })}
      {state.routes.map((route, index) => {
        const { options } = descriptors[route.key];
        const focused = state.index === index;
        const color = focused ? colorPalette.secondary[600] : colorPalette.gray[500];
        const rawLabel = options.tabBarLabel ?? options.title;
        const label = typeof rawLabel === 'string' ? rawLabel : '';

        const onPress = () => {
          const event = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });
          if (!focused && !event.defaultPrevented) {
            navigation.navigate(route.name);
          }
        };

        return (
          <PlatformPressable
            key={route.key}
            onPress={onPress}
            style={[tabNavigatorStyle.tabBarItem, styles.tabItem]}
            accessibilityRole="tab"
            accessibilityState={{ selected: focused }}
            accessibilityLabel={label || route.name}
            testID={`tab-${route.name}`}
          >
            {typeof options.tabBarIcon === 'function'
              ? options.tabBarIcon({ focused, color, size: TAB_ICON_SIZE })
              : getTabBarIcon(route.name, focused, color, TAB_ICON_SIZE)}
            {label ? (
              <Text style={[tabNavigatorStyle.tabBarLabel, { color }]} numberOfLines={1}>
                {label}
              </Text>
            ) : null}
          </PlatformPressable>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  barRow: {
    flexDirection: 'row',
  },
  tabItem: {
    flex: 1,
  },
  indicatorBase: {
    position: 'absolute',
    top: INDICATOR_TOP,
    left: INDICATOR_MARGIN + INDICATOR_SIDE_PADDING,
    borderRadius: INDICATOR_RADIUS,
    backgroundColor: colorPalette.secondary[100],
  },
  anchorOverlay: {
    position: 'absolute',
    top: INDICATOR_TOP,
  },
});

export default AnimatedTabBar;
